import React from 'react';
import Loading from '../components/Loading';
import { useAuth } from '../contexts/AuthProvider';
import ForbiddenAccessPage from '../pages/ForbiddenAccessPage';
import { checkUserSignOnStatus } from '../services/signOnService';

const ALLOWED_BILLING_ROLES = new Set(['CASHIER', 'ADMIN']);

const normalizeRole = (value: string | null | undefined): string =>
    (value || '').trim().toUpperCase();

const hasBillingRoleAccess = (role: string | null | undefined): boolean =>
    ALLOWED_BILLING_ROLES.has(normalizeRole(role));

type GuardStatus = 'idle' | 'checking' | 'signed-on' | 'not-signed-on' | 'error';

const CashierSignOnGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, loading: authLoading } = useAuth();
    const [guardStatus, setGuardStatus] = React.useState<GuardStatus>('idle');

    const hasRoleAccess = hasBillingRoleAccess(user?.user_role as string | null | undefined);

    React.useEffect(() => {
        if (authLoading || !hasRoleAccess) {
            return;
        }

        if (!user?.user_name) {
            setGuardStatus('not-signed-on');
            return;
        }

        let isMounted = true;

        const verifySignOn = async () => {
            setGuardStatus('checking');
            try {
                const status = await checkUserSignOnStatus(user.user_name);
                if (!isMounted) {
                    return;
                }
                setGuardStatus(status.signon === 1 ? 'signed-on' : 'not-signed-on');
            } catch {
                if (!isMounted) {
                    return;
                }
                setGuardStatus('error');
            }
        };

        void verifySignOn();

        return () => {
            isMounted = false;
        };
    }, [authLoading, hasRoleAccess, user?.user_name]);

    if (authLoading || (hasRoleAccess && (guardStatus === 'idle' || guardStatus === 'checking'))) {
        return <Loading message="Checking cashier sign on..." />;
    }

    if (!hasRoleAccess) {
        return (
            <ForbiddenAccessPage message="Cashier or admin role is required to access checkout." />
        );
    }

    if (guardStatus === 'not-signed-on' || guardStatus === 'error') {
        return (
            <ForbiddenAccessPage message="Cashier sign in is required to access checkout." />
        );
    }

    return <>{children}</>;
};

export default CashierSignOnGuard;